import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, Injector } from '@angular/core';
import { DatePipe } from '@angular/common';
import { BaseControlComponent } from '../base-control/base-control.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

/**
 * Component for selecting a date using a calendar popup.
 * Extends BaseControlComponent for common form control functionality.
 * Manages an internal FormGroup for the p-calendar component and synchronizes
 * its value with the parent FormGroup's control.
 */
@Component({
	selector: 'app-date-select',
	templateUrl: './date-select.component.html',
	styleUrls: ['./date-select.component.scss'],
	providers: [DatePipe]
})
/**
 * DateSelectComponent provides a date picker integrated with Angular forms.
 * It displays and accepts dates in DD-MM-YYYY format, but communicates with the form as YYYY-MM-DD.
 * Handles default-to-today logic, validation, and emits value changes.
 */
export class DateSelectComponent extends BaseControlComponent implements OnInit, OnDestroy {
	/**
	 * If true, sets the default date to the current day if the parent control has no initial value.
	 * @type {boolean}
	 */
	@Input() defaultToToday: boolean = true;
	/**
	 * Emits the selected date as a 'yyyy-MM-dd' formatted string whenever the date changes.
	 * @type {EventEmitter<string | null>}
	 */
	@Output() dateChange = new EventEmitter<string | null>();

	/** Internal value for p-calendar's ngModel */
	_calendarValue: Date | null = null;

	/**
	 * Injects required services and initializes the internal dateForm.
	 * Passes necessary dependencies to the BaseControlComponent constructor.
	 * @param {DatePipe} datePipe - Service for formatting date values.
	 * @param {Injector} injector - Injector for dependency injection.
	 * @param {TranslateService} translate - Service for handling translations (passed to base).
	 * @param {ChangeDetectorRef} cdr - Service for triggering change detection (passed to base).
	 * @param {MessageService} messageService - Service for displaying messages (passed to base).
	 */
	constructor(
		private datePipe: DatePipe,
		protected override injector: Injector,
		protected override translate: TranslateService,
		protected override cdr: ChangeDetectorRef,
		protected override messageService: MessageService
	) {
		super(injector, translate, cdr, messageService);
	}

	/**
	 * Initializes the component by calling the base class ngOnInit,
	 * synchronizing the initial date value from the parent control to the internal form,
	 * setting the default date if applicable, and subscribing to parent control value changes.
	 * @override
	 */
	override ngOnInit(): void {
		super.ngOnInit();
		if (this.control) {
if (this._value === null && this.defaultToToday) {
				const todayDateObject = new Date();
				const todayString = this.datePipe.transform(todayDateObject, 'yyyy-MM-dd');
this.control.setValue(todayString, { emitEvent: false });
				this.writeValue(todayString);
				this.dateChange.emit(todayString);
				this.cdr.markForCheck();
			}
			this.parentValueSub = this.control.valueChanges.subscribe(parentValue => {
if (parentValue === null && this.defaultToToday) {
					const todayDateObject = new Date();
					const todayString = this.datePipe.transform(todayDateObject, 'yyyy-MM-dd');
this.control?.setValue(todayString, { emitEvent: false });
					this.writeValue(todayString);
					this.dateChange.emit(todayString);
					this.cdr.markForCheck();
				} else {
					this.writeValue(parentValue);
				}
			});
		}
	}

	/**
	 * Writes a value from the parent form into the component.
	 * Converts string date to Date object for the calendar.
	 * @param {any} obj - The value from the parent form (should be a string date).
	 */
	override writeValue(obj: any): void {
const dateString = typeof obj === 'string' ? obj : null;
		if (dateString !== this._value) {
			this._value = dateString;
			this._calendarValue = this.parseDateString(dateString);
			this.cdr.markForCheck();
		}
	}

	/**
	 * Handles the date selection or clearing event from the p-calendar component.
	 * Updates _calendarValue, formats the date, propagates the string value, and emits event.
	 * @param {Date | null} selectedDate - The Date object selected in the calendar, or null if cleared.
	 */
	onCalendarChange(selectedDate: Date | null): void {
this._calendarValue = selectedDate;
		const formattedValue = selectedDate ? this.datePipe.transform(selectedDate, 'yyyy-MM-dd') : '';
		this.control?.setValue(formattedValue, { emitEvent: true });
		this.propagateChange(formattedValue);
		this.dateChange.emit(formattedValue);
		this.markAsInteracted();
		this.control?.updateValueAndValidity();
		this.cdr.markForCheck();
	}

	/**
	 * Handles the input event from the p-calendar component.
	 * Parses the input value, updates the internal state, and propagates the change.
	 * @param {string} inputValue - The value entered in the input field.
	 */
	onCalendarInput(inputValue: string): void {
const parsedDate = this.parseDateString(inputValue);
		this._calendarValue = parsedDate;
		const formattedValue = parsedDate ? this.datePipe.transform(parsedDate, 'yyyy-MM-dd') : '';
		this.control?.setValue(formattedValue, { emitEvent: true });
		this.propagateChange(formattedValue);
		this.dateChange.emit(formattedValue);
		this.markAsInteracted();
		this.control?.updateValueAndValidity();
		this.cdr.markForCheck();
	}

	/**
	 * Parses a date string (potentially in 'yyyy-MM-dd', 'dd-MM-yyyy', or 'dd/MM/yyyy' format)
	 * into a JavaScript Date object. Logs a warning if the format is unrecognized.
	 * @param {string | null} dateString - The date string to parse.
	 * @returns {Date | null} The parsed Date object, or null if parsing fails or input is null.
	 * @private
	 */
	private parseDateString(dateString: string | null): Date | null {
		if (!dateString) return null;
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			const parts = dateString.split('-');
			if (parts.length === 3) {
				return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
			}
		}
		if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
			const parts = dateString.split('-');
			if (parts.length === 3) {
				return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
			}
		}
		if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
			const parts = dateString.split('/');
			if (parts.length === 3) {
				return new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
			}
		}

		console.warn(`[DateSelect] Could not parse date string: ${dateString}`);
		return null;
	}
}